import React from 'react';
import PendingDot from '../PendingDot';
import { format } from 'date-fns';
import { useStudents } from '../../data/useStudents';
import useNames from '../../hooks/useNames';
import StatusGlyph from './StatusGlyph';
import { categoryPaint } from './eventPaint';
import { asDate } from './weekGrid';
import { useTimeFormat } from '../../context/LocaleContext';
import s from './LessonCard.module.css';

// One lesson on the week grid.
//
// The fill is the category's and attendance is `StatusGlyph` (#130; the
// argument is at `categoryPaint` in eventPaint.js), as on every view. The tint
// is a `color-mix` in the stylesheet, against `--surface` and the ramp. A
// lesson called off is the `Teacher Cancelled` attendance status, which the
// glyph draws; a removed entry is not produced by the rule (#152).
//
// ── One card per occurrence, and a narrow one (#749) ───────────────────────
//
// The time grids hand this one row per occurrence (`blocks`,
// useCalendarOccurrences.js), so a group lesson is one card whose name is the
// core's `2 students` — MMS's default wording — with the students' names
// written for a screen reader only. `narrow` is a card sharing its column with
// an overlapping one (half, a third): MMS keeps a half-width block to three
// lines, its time, its title and its category, and so does this, each line
// cut with an ellipsis rather than wrapped, and no duration, which is the
// block's height already.
//
// ── The public description, inline after the category (#796) ───────────────
//
// `lesson.description` is the occurrence's `calendarDescription` — the public
// description while its series ticks "Show public description directly on
// calendar", else empty (`calendar_description` in core/src/schedule.rs). MMS's
// Day and Week Agenda, in its default Detailed event view, write it in small
// regular text in the block's own ink straight after the category
// ("Recital  Bring the duet part"), wrapping, and cut off by the block's
// height. A narrow card's category line does the same, so it is no longer one
// line when there is a description to follow it.

const CARD = {
  plain: s.card,
  tinted: s.cardTinted,
};

export default function LessonCard({
  lesson,
  categoryColorFor,
  onClick,
  onResizeStart,
  onMoveStart,
  compact = false,
  narrow = false,
  style,
}) {
  // `pendingWrite` is stamped by `occurrencesBetween` in domain/schedule.js
  // (#407) and carried flat by useCalendarOccurrences.js.
  const formatTime = useTimeFormat();
  const pending = lesson.pendingWrite;
  const description = lesson.description || '';
  const date = asDate(lesson.date);
  // The Region's clock (#534): `4:00 PM` in the United States, `16:00` in
  // Germany, as MMS writes them.
  const timeStr = formatTime(format(date, 'HH:mm'));

  // A lesson whose write has not landed wears the pending tint instead of its
  // category's: "is this saved" is the more urgent question, and the card has
  // one fill. The category comes back when the write does.
  const paint = pending
    ? { className: s.cardPending }
    : categoryPaint(lesson, categoryColorFor, CARD);

  // Prefer the student's current name from the live cache, so a rename shows
  // on the calendar without re-saving each lesson. Falls back to the stamped
  // `lesson.studentName` when the lookup cannot answer: an offline cache miss,
  // a deleted student, or one outside the listener's window.
  const { getStudent } = useStudents();
  const names = useNames();
  const displayName =
    names.student(getStudent(lesson.studentId), lesson.studentName || 'Unknown');
  // A group's `2 students` says who to a screen reader.
  const who = lesson.attendeeNames ? <span className="sr-only">: {lesson.attendeeNames}</span> : null;
  const badges = (
    <div className={s.badges}>
      <StatusGlyph status={lesson.status} />
      <PendingDot pending={pending} />
    </div>
  );

  return (
      <div
        {...paint}
        style={{ ...paint.style, ...style }}
        // The drag hook reads this to tell a press on a lesson from a press on
        // the empty column under it (startCreate in src/hooks/useLessonDrag.js).
        data-lesson-card=""
        onClick={(e) => onClick?.(e)}
        // Picking the lesson up to move it (#571). The resize strip below stops
        // its own press from reaching here, so the two gestures cannot both
        // start; a press that never travels is still the click above.
        onPointerDown={onMoveStart ? (e) => onMoveStart(lesson, e) : undefined}
        role="button"
        tabIndex={0}
        // The event goes with it: both views read `e.currentTarget` to anchor
        // the popover.
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(e);
          }
        }}
      >
        {narrow && !lesson.allDay ? (
          <div className={s.narrowLines}>
            <div className={s.header}>
              <span className={s.line}>{timeStr}</span>
              {badges}
            </div>
            <span className={s.nameLine}>{displayName}{who}</span>
            {description ? (
              <span className={s.lineDescribed}>
                {lesson.categoryLabel ? <span className={s.category}>{lesson.categoryLabel}</span> : null}
                <span className={s.description}>{description}</span>
              </span>
            ) : lesson.categoryLabel ? <span className={s.line}>{lesson.categoryLabel}</span> : null}
          </div>
        ) : (
          <div className={s.header}>
            <span className={s.name}>{displayName}{who}</span>
            {badges}
          </div>
        )}
        {!compact && !(narrow && !lesson.allDay) && (
          <div className={!description ? s.meta : lesson.allDay ? s.metaDescribedAllDay : s.metaDescribed}>
            {/* An all-day event says so where the clock would go and prints no
                duration (#160). The document still carries both (`allDay` in
                firestore.rules); this is the rule that says they are not to be
                read. */}
            {lesson.allDay ? (
              <span>All Day</span>
            ) : (
              <>
                <span>{timeStr}</span>
                <span className={s.dot} />
                <span>{lesson.durationMin}min</span>
              </>
            )}
            {/* The category's own name, spelled the way the studio spells it. */}
            {lesson.categoryLabel ? (
              <>
                <span className={s.dot} />
                <span>{lesson.categoryLabel}</span>
              </>
            ) : null}
            {description ? <span className={s.description}>{description}</span> : null}
          </div>
        )}
        {onResizeStart && (
          <div
            className={s.resize}
            onPointerDown={(e) => {
              // Don't let the resize gesture also fire the card's onClick or
              // start a column drag-to-create — both would race against the
              // resize state machine.
              e.stopPropagation();
              onResizeStart(lesson, e);
            }}
          />
        )}
    </div>
  );
}
